import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { bundles, featuredBundle, images, reviews } from '../../data/blackout';
import { useBundle } from '../../contexts/BundleContext';
import { goToCheckout } from '../../utils/checkout';
import { formatPrice } from '../../utils/currency';
import { scrollToSection } from '../../utils/scroll';
import { StarRow } from './StarRow';
const identityWords = ['Identity', 'Freedom', 'Crew', 'Real life'];
export function HeroSection() {
  const {
    showCrystalBonus
  } = useBundle();
  const offer = featuredBundle;
  const single = bundles[0];
  const average = reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length;
  return <section id="top" className="relative overflow-hidden bg-obsidian" data-id="element-182" data-mp="223uxj7lb0xkp">
      <div className="relative lg:min-h-[calc(100svh-97px)]" data-id="element-183" data-mp="14o7fc6rfrq4n">
        <div className="relative h-[52svh] min-h-[340px] overflow-hidden lg:absolute lg:inset-0 lg:h-auto" data-id="element-184" data-mp="14nxko5vg4usm">
          <img src={images.hero} alt="Hooded figure wearing the BU-01 Ultimate Mask in a dark room" className="absolute inset-0 h-full w-full object-cover object-[68%_center] lg:object-center" data-id="element-185" data-mp="19l7io9wwies2" />
          <div className="halftone absolute inset-0" aria-hidden="true" data-id="element-186" data-mp="hj0rcumtyz6e" />
          <div className="grain absolute inset-0" aria-hidden="true" data-id="element-187" data-mp="ckh5svc5fpek" />
        </div>

        <div className="relative mx-auto flex max-w-7xl lg:min-h-[calc(100svh-97px)] lg:items-center" data-id="element-188" data-mp="cid3ghtweyge">
          <motion.div initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          ease: [0.23, 1, 0.32, 1]
        }} className="w-full px-5 pb-14 pt-8 md:px-8 lg:max-w-[580px] lg:py-16" data-id="element-189" data-mp="7j06h49bcb03">
            <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-steel" data-id="element-190" data-mp="3psk9wjk4houq">
              Blackout Unit™ · Issue 01 // Nocturne
            </p>

            <h1 className="mt-4 font-display text-[50px] uppercase leading-[0.92] text-bone sm:text-7xl lg:text-[80px]" data-id="element-191" data-mp="1nz34znndn23o">
              This isn’t a costume.
              <span className="mt-1 block" data-id="element-192" data-mp="2724wds7wuxyr">It’s a transformation.</span>
            </h1>
            <span aria-hidden="true" className="mt-5 block h-[3px] w-40 -rotate-1 bg-signal" data-id="element-193" data-mp="2bw944dp03a0w" />

            <p className="mt-6 font-display text-2xl uppercase leading-tight text-bone/90 md:text-[28px]" data-id="element-194" data-mp="1y54abs8bdbyh">
              One mask changes the look.
              <span className="block text-steel" data-id="element-195" data-mp="2qsi4jikmntew">A crew changes the whole room.</span>
            </p>
            <p className="mt-4 max-w-md text-[15px] leading-relaxed text-bone/75" data-id="element-196" data-mp="2uubzrbc2057o">
              BU-01 turns a basic outfit into a completely different character:
              an unmistakable skull silhouette and a dark, anonymous presence.
            </p>

            {/* Primary offer — the 3-mask bundle leads, not the single mask */}
            <div className="mt-8 flex items-center gap-4 border border-bone/40 bg-obsidian/85 p-3 pr-4 backdrop-blur-sm" data-id="element-197" data-mp="15dax89njx6li">
              <img src={images.productFront} alt="BU-01 Ultimate Mask" className="h-[72px] w-[72px] shrink-0 border border-gunmetal object-cover" data-id="element-198" data-mp="1abvymcejhwbj" />
              <div className="min-w-0 flex-1" data-id="element-199" data-mp="xxpf18x0pi80">
                <p className="font-display text-2xl uppercase leading-none text-bone" data-id="element-200" data-mp="1bu30et4oa3rr">
                  {offer.deal}
                </p>
                <p className="mt-1.5 text-[12px] leading-snug text-steel" data-id="element-201" data-mp="16tyg0s0n8ow9">
                  {offer.count} masks · Free U.S. shipping
                  {showCrystalBonus && offer.bonus && <span className="block text-bone/85" data-id="element-202" data-mp="f6pvin8al0m4">+ Bonus color crystal set</span>}
                </p>
              </div>
              <div className="shrink-0 text-right" data-id="element-203" data-mp="o51lcn1c3udi">
                <p className="font-display text-3xl leading-none text-bone" data-id="element-204" data-mp="o3gr6yixmdg6">
                  {formatPrice(offer.price)}
                </p>
                <p className="mt-1 text-sm text-steel line-through" data-id="element-205" data-mp="3rmtkcldw1el7">
                  {formatPrice(offer.regularPrice)}
                </p>
              </div>
            </div>

            <button type="button" onClick={() => goToCheckout(offer)} className="group mt-3 inline-flex h-14 w-full items-center justify-center gap-3 bg-bone px-8 text-[13px] font-semibold uppercase tracking-[0.2em] text-obsidian transition-[background-color,transform] duration-150 ease-out hover:bg-white active:scale-[0.98]" data-id="element-206" data-mp="e2pjgcc6m8u1">
              {offer.cta} · {offer.count} masks
              <ArrowRight className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-0.5" aria-hidden="true" data-id="element-207" data-mp="wse9ps539132" />
            </button>

            <div className="mt-4 flex flex-col gap-3 text-[13px] sm:flex-row sm:items-center sm:justify-between" data-id="element-208" data-mp="16qbwscb7oru3">
              <button type="button" onClick={() => goToCheckout(single)} className="self-start text-steel underline decoration-gunmetal underline-offset-4 transition-colors duration-150 hover:text-bone hover:decoration-bone" data-id="element-209" data-mp="33kyiw69zpq11">
                Just want one? 1 mask — {formatPrice(single.price)} + shipping
              </button>
              <button type="button" onClick={() => scrollToSection('reviews')} className="flex items-center gap-2 self-start text-bone/75 transition-colors duration-150 hover:text-bone" data-id="element-210" data-mp="1gw3fn3abbcny">
                <StarRow rating={average} data-id="element-211" data-mp="24kwep8m8e0ms" />
                {average.toFixed(1)} average rating
              </button>
            </div>
          </motion.div>

          {/* Desktop: the real product, framed like a specimen */}
          <aside className="absolute right-8 top-1/2 hidden w-56 -translate-y-1/2 xl:block" data-id="element-212" data-mp="1un8azt6ndish">
            <p className="mb-6 rotate-[-8deg] font-script text-3xl leading-tight text-bone/85" data-id="element-213" data-mp="o75yx4lr1nxb">
              Same world.
              <br data-id="element-214" data-mp="j6ibeswqbp7u" />
              Different you.
            </p>
            <div className="border border-bone/30 bg-obsidian/70 p-2 backdrop-blur-sm" data-id="element-215" data-mp="koypjuezi5ba">
              <img src={images.productFront} alt="BU-01 Ultimate Mask, front" className="aspect-square w-full object-cover grayscale" data-id="element-216" data-mp="pntgxkr0p270" />
            </div>
            <div className="mt-3 flex items-end justify-between gap-3" data-id="element-217" data-mp="gmvf9941x6rl">
              <p className="rotate-[-6deg] font-script text-3xl text-bone" data-id="element-218" data-mp="goybiavsrw4r">BU-01</p>
              <ul className="border border-bone/25 px-3 py-2 text-[9px] uppercase leading-[1.9] tracking-[0.3em] text-steel" data-id="element-219" data-mp="1wwdkwdafd7d">
                {identityWords.map(w => <li key={w} data-id="element-220" data-mp="3p488x5oqsd67">{w}</li>)}
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </section>;
}